import { GeolocationContainer } from "@/lib/geolocation/container"
import { GeolocationContext } from "@/lib/geolocation/context"
import { distanceOptionToKey } from "@/lib/geolocation/distance/distance"
import type { ProcessLocking } from "@/lib/process_locking"

export namespace Geolocation {
  export type BrowserState = {
    browserSupport: boolean
    permission: PermissionStatus
    position?: GeolocationPosition
    positionError?: GeolocationPositionError
    timedOut: boolean
  }

  export type ContextData = {
    address?: Bing.Address
    chooseDistance: Functions.ChooseDistance
    chooseSuggestion: Functions.ChooseSuggestion
    fetchSuggestions: Functions.FetchSuggestions
    formattedAddress?: string
    latlng?: Latlng
    permissionDenied: boolean
    position?: Position
    processing: string | void
    reset: Functions.Reset
    distance: DistanceOption
    distanceKey: DistanceKey
    suggestions: Suggestion[]
    useMyLocation: Functions.UseMyLocation
  }

  export const Container = GeolocationContainer
  export const Context = GeolocationContext

  export type Details = BrowserState & {
    formattedAddress: string
    geometry: Geometry
  }

  export type Geometry = {
    location: Latlng
    location_type: string
    viewport: {
      northeast: Latlng
      southwest: Latlng
    }
  }

  export type Position = string

  export type DistanceOption = 5 | 10 | 20 | 50

  export type DistanceKey = `5km` | `10km` | `20km` | `50km`

  export const defaultDistanceOption: DistanceOption = 20

  export const defaultDistanceKey = distanceOptionToKey(defaultDistanceOption)

  export const distanceOptions: DistanceOption[] = [5, 10, 20, 50]

  export const distanceKeys = distanceOptions.map(distanceOptionToKey)

  export const maximumDistanceOption = distanceOptions[
    distanceOptions.length - 1
  ] as DistanceOption

  export const maximumDistanceKey = distanceKeys[
    distanceKeys.length - 1
  ] as DistanceKey

  export type Latlng = {
    lat: number
    lng: number
  }

  export type Suggestion = Bing.Address

  export namespace Bing {
    export type Address = {
      addressLine: string
      adminDistrict: string
      adminDistrict2: string
      countryRegion: string
      countryRegionIso2: string
      formattedAddress: string
      landmark: string
      locality: string
      neighbourhood: string
      postalCode: string
      streetName: string
    }

    export namespace AutosuggestAPI {
      export type EntityType = `Place` | `Address` | `Business`

      export type EntityTypeOptions = {
        entityType: EntityType
        maxResults: number
      }[]

      export type Resource = {
        __type: `Address`
        address: Address
      }

      export type ResourceContainer = {
        __type: `Autosuggest:http://schemas.microsoft.com/search/local/ws/rest/v1`
        value: Resource[]
      }

      export type ResourceSet = {
        estimatedTotal: number
        resources: ResourceContainer[]
      }

      export type Response = {
        resourceSets: ResourceSet[]
      }
    }

    export namespace LocationAPI {
      // SouthLatitude, WestLongtitude, NorthLatitude, EastLongtitude
      export type BoundingBox = [number, number, number, number]

      export type GeocodePoint = Point & {
        calculationMethod:
          | `Interpolation`
          | `InterpolationOffset`
          | `Parcel`
          | `Rooftop`
        usageTypes: `Display` | `Route`
      }

      export type QueryParseValue = {
        property:
          | `AddressLine`
          | `AdminDistrict`
          | `AdminDistrict2`
          | `CountryRegion`
          | `CountryRegionIso2`
          | `FormattedAddress`
          | `Landmark`
          | `Locality`
          | `Neighbourhood`
          | `PostalCode`
        value: string
      }

      export type Point = {
        coordinates: [number, number]
        type: `Point`
      }

      // https://learn.microsoft.com/en-us/bingmaps/rest-services/locations/location-data
      export type Resource = {
        address: Address
        bbox: BoundingBox
        entityType: string
        name: string
        point: Point
        confidence: `High` | `Medium` | `Low`
        matchCode: `Good` | `Ambiguous` | `UpHeirarchy`
        queryParseValues: QueryParseValue[]
        geocodePoints: GeocodePoint[]
      }

      export type ResourceSets = {
        estimatedTotal: number
        resources: Resource[]
      }

      // https://learn.microsoft.com/en-us/bingmaps/rest-services/common-response-description
      export type Response = {
        statusCode: number
        statusDescription: string
        authenticationResultCode:
          | `ValidCredentials`
          | `InvalidCredentials`
          | `CredentialsExpired`
          | `NotAuthorized`
          | `NoCredentials`
          | `None`
        traceId: string
        copyright: string
        brandLogoUri: string
        resourceSets: ResourceSets[]
      }
    }
  }

  export namespace GoogleGeocodeAPI {
    export type Response = {
      plus_code: {
        compound_code: string
        global_code: string
      }
      results: Result[]
      status: string
    }

    export type Result = {
      address_components: {
        long_name: string
        short_name: string
        type: string[]
      }[]
      formatted_address: string
      geometry: Geometry
      place_id: string
      plus_code: {
        compound_code: string
        global_code: string
      }
      types: string[]
    }
  }

  export namespace Functions {
    export type ChooseDistance = (
      distance: DistanceKey | DistanceOption
    ) => Promise<void>

    export type ChooseSuggestion = (args: {
      suggestion: Geolocation.Suggestion
    }) => Promise<{
      address: Bing.Address | void
      position: Geolocation.Position | void
    }>

    export type FetchSuggestions = (args: {
      entityTypes?: Bing.AutosuggestAPI.EntityTypeOptions
      input: string
    }) => Promise<boolean>

    export type Reset = () => void

    export type UseMyLocation = () => Promise<
      ProcessLocking.Conclusion<{
        address: Bing.Address | void
        position: Geolocation.Position | void
      }>
    >
  }
}
